/**
 * Src-ref asset validator.
 *
 * Used by callers that publish scene data as a standalone `.gltf` document (no
 * bundled `assets/` folder, no folder to resolve against). Every asset
 * reference must already be a usable absolute URL — local paths, built-in
 * primitives and transient `blob:` URLs all need resolving to a hosted URL
 * before publishing.
 *
 * This walks SCENE DATA. Its counterpart, `manifestValidator.ts`, walks a
 * written document's `files[]` and classifies each entry into the three
 * manifest classes (spec §2.5) — that is the pre-flight for a FOLDER save,
 * where a relative `assets/…` uri is the normal case rather than an offence.
 * The two share one predicate, {@link isAbsoluteHttpUrl}: "absolute" is
 * http(s) WITH a host, never a raw `startsWith('http')`, so a leading-slash
 * path cannot pass as absolute (the Unix `file:` landmine, tb#848).
 */

import type { SceneObjectData } from '../scenedata/sceneobjectdata';
import { isPropertyBinding } from '../behaviors/tracebehavior';
import { isAbsoluteHttpUrl } from './manifestValidator';
import {
    BEHAVIOR_ASSET_FIELDS,
    BEHAVIOR_NESTED_ASSET_FIELDS,
    NESTED_SRC_RECURSION_DEPTH,
    isProvenanceHistoryField,
} from './behaviorAssetFields';

/** Offending asset reference found during validation. */
export interface SrcRefOffender {
    /** The node path (e.g. "Scene/Cube") where the offending reference lives. */
    nodePath: string;
    /** The field on the node or behavior whose value isn't a usable URL. */
    field: string;
    /** The actual offending value. */
    value: string;
}

export interface SrcRefValidationResult {
    ok: boolean;
    offenders: SrcRefOffender[];
}

/**
 * Walk scene data and report every asset reference that isn't an absolute
 * http(s) URL with a host. `blob:` URLs are rejected too — they're transient
 * and would 404 after a page reload.
 */
export function validateSrcRefAssets(sceneData: SceneObjectData[]): SrcRefValidationResult {
    const offenders: SrcRefOffender[] = [];

    for (const data of sceneData) {
        const nodePath = computeNodePath(data);
        if (data.src && !isAbsoluteHttpUrl(data.src)) {
            offenders.push({ nodePath, field: 'src', value: data.src });
        }
        if (data.behaviors) {
            for (const behavior of data.behaviors) {
                collectBehaviorOffenders(behavior, nodePath, offenders);
            }
        }
    }

    return { ok: offenders.length === 0, offenders };
}

/** Format a result for human display (one line per offender). */
export function formatSrcRefOffenders(offenders: SrcRefOffender[]): string {
    return offenders
        .map(o => `  • ${o.nodePath} (${o.field}): ${o.value}`)
        .join('\n');
}

// ── Internals ─────────────────────────────────────────────────────────

function computeNodePath(data: SceneObjectData): string {
    const parent = data.transform?.parent;
    if (!parent) return data.name;
    const cleanParent = parent.startsWith('/') ? parent.slice(1) : parent;
    return `${cleanParent}/${data.name}`;
}

function collectBehaviorOffenders(
    behavior: unknown,
    nodePath: string,
    offenders: SrcRefOffender[],
): void {
    if (!behavior || typeof behavior !== 'object') return;
    const obj = behavior as Record<string, unknown>;
    const behaviorLabel = typeof obj['@type'] === 'string' ? obj['@type'] : 'behavior';
    // Dedup key for {nodePath, value} to avoid double-reporting an offender
    // when both a top-level field and the recursive scan pick up the same
    // `src` reference — those two scanners label the same site differently
    // (`Renderer.materials.src` vs `Renderer.src`), so the value is the only
    // stable identity for "one authored site, seen twice".
    const seen = new Set<string>();
    const push = (field: string, value: string): void => {
        const key = `${nodePath}|${value}`;
        if (seen.has(key)) return;
        seen.add(key);
        offenders.push({ nodePath, field, value });
    };

    // Properties sites (declaration values, state overrides, sheet refs) are
    // each a REAL distinct authored location — the same bad URL in a
    // declaration and in a state override needs fixing in both places, so
    // these dedup by {field, value}, not value alone. They can't collide with
    // the scanners above (declaration and state values never live under a
    // `src` key, and `sheets` is reported by index).
    const seenFields = new Set<string>();
    const pushField = (field: string, value: string): void => {
        const key = `${nodePath}|${field}|${value}`;
        if (seenFields.has(key)) return;
        seenFields.add(key);
        offenders.push({ nodePath, field, value });
    };

    for (const prop of BEHAVIOR_ASSET_FIELDS) {
        const value = obj[prop];
        if (isOffendingAssetValue(value)) {
            push(`${behaviorLabel}.${prop}`, value);
        }
    }

    for (const { parent, key } of BEHAVIOR_NESTED_ASSET_FIELDS) {
        const nested = obj[parent] as Record<string, unknown> | undefined;
        const value = nested?.[key];
        if (isOffendingAssetValue(value)) {
            push(`${behaviorLabel}.${parent}.${key}`, value);
        }
    }

    findNestedSrcOffenders(obj, nodePath, behaviorLabel, push);

    if (obj['@type'] === 'Properties') {
        collectSheetOffenders(obj, pushField);
        collectPropertiesAssetOffenders(obj, pushField);
    }
}

/**
 * A value offends when it's a non-empty string that is neither a hosted URL
 * nor a property binding. Bindings (`"$material"`, `"{theme}"`) are not URLs —
 * the URL they resolve to is validated where it's authored: in the Properties
 * declaration / state values (see collectPropertiesAssetOffenders).
 */
export function isOffendingAssetValue(value: unknown): value is string {
    return typeof value === 'string'
        && value.length > 0
        && !isAbsoluteHttpUrl(value)
        && !isPropertyBinding(value);
}

/**
 * Validate the external sheet URLs a Properties behavior references.
 *
 * `PropertiesData.sheets` is an asset reference like any other `src`: the
 * runtime fetches each URL (`Properties._fetchSheet`) to get the declarations —
 * and, once a style IS a sheet, the applied style itself — so a local
 * `assets/theme.json` path that survives publish means the published scene
 * resolves no declarations at all and every bound slot silently falls back to
 * its authored material.
 *
 * It is checked here rather than through `BEHAVIOR_ASSET_FIELDS` because that
 * list is read as `obj[field]` and typed as a single string; `sheets` is an
 * array, and each entry is its own authored site (hence `pushField`, whose
 * dedup key includes the index).
 */
function collectSheetOffenders(
    props: Record<string, unknown>,
    push: (field: string, value: string) => void,
): void {
    const sheets = props.sheets;
    if (!Array.isArray(sheets)) return;
    for (const [index, value] of sheets.entries()) {
        if (isOffendingAssetValue(value)) {
            push(`Properties.sheets[${index}]`, value);
        }
    }
}

/**
 * Validate asset URLs held in Properties declarations. A string declaration
 * with an asset `kind` (e.g. `material`) feeds consumers like
 * `Renderer.materials[].src` via bindings, so its base value and every state
 * override must be hosted URLs at publish time — a local path inside a state
 * would 404 when the state activates in the published scene.
 *
 * States are validated here too: a state's values apply to this node's own
 * declarations, so a local path inside one would 404 the moment the state
 * activates in the published scene.
 */
function collectPropertiesAssetOffenders(
    props: Record<string, unknown>,
    push: (field: string, value: string) => void,
): void {
    const declarations = Array.isArray(props.properties)
        ? props.properties as Array<Record<string, unknown>>
        : [];

    const assetPropertyNames = new Set<string>();
    for (const decl of declarations) {
        if (typeof decl?.name !== 'string' || typeof decl.kind !== 'string') continue;
        assetPropertyNames.add(decl.name);
        if (isOffendingAssetValue(decl.value)) {
            push(`Properties.${decl.name}`, decl.value);
        }
    }
    if (assetPropertyNames.size === 0) return;

    const states = props.states as Record<string, { values?: Record<string, unknown> }> | undefined;
    if (states && typeof states === 'object') {
        for (const [stateName, stateDef] of Object.entries(states)) {
            if (!stateDef?.values) continue;
            for (const [propName, value] of Object.entries(stateDef.values)) {
                if (assetPropertyNames.has(propName) && isOffendingAssetValue(value)) {
                    push(`Properties.states.${stateName}.${propName}`, value);
                }
            }
        }
    }
}

function findNestedSrcOffenders(
    obj: unknown,
    nodePath: string,
    behaviorLabel: string,
    push: (field: string, value: string) => void,
    depth = 0,
    visited: WeakSet<object> = new WeakSet(),
): void {
    if (depth > NESTED_SRC_RECURSION_DEPTH || !obj || typeof obj !== 'object') return;
    if (visited.has(obj)) return;
    visited.add(obj);

    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
        if (isProvenanceHistoryField(obj as Record<string, unknown>, key)) continue;
        if (key === 'src' && isOffendingAssetValue(value)) {
            push(`${behaviorLabel}.${key}`, value);
        } else if (typeof value === 'object' && value !== null) {
            findNestedSrcOffenders(value, nodePath, behaviorLabel, push, depth + 1, visited);
        }
    }
}
